/**
 * Box Truck → My Drivers. The operator's box-truck drivers — dispatcher-variant StandaloneUsers, either
 * claimed from Driver Verifications. The per-driver detail is read-first: profile, documents and messaging.
 * Driver creation, status, app access and profile editing live in Profiles → Drivers so this operational
 * page cannot create a second management path.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft, Building2, FileText, IdCard, Search, Truck } from 'lucide-react'
import { MessagingServicesSection } from 'pages/dashboard/pages/profiles/pages/drivers/components/driver-detail/MessagingServicesSection'
import { Badge, Button, ProgressBar, Spinner } from '@/components/ds2'
import { TeamGroupBadge } from '@/components/TeamGroupBadge'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { useDebouncedValue } from 'hooks/useDebouncedValue'
import { boxTruckStatusBadge, BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS } from '../utils/format'
import { canEditLocation, hasAppAccount } from '../utils/rowCapabilities'
import { BOX_TRUCK_ASSET_DOC_LABELS, BOX_TRUCK_OWNER_DOC_LABELS } from '@numeo/types-core'
import { BOX_TRUCK_RECRUIT_SMS } from '../utils/driverSmsTemplate'
import { BoxTruckLastLocationCell } from '../components/BoxTruckLastLocationCell'
import { DriverAvailabilityCell, DriverNoteCell } from '../components/MyDriverAvailabilityNote'
import { MyDriverQuickViewWithCompanyDeposit } from '../components/MyDriverQuickViewWithCompanyDeposit'
import { DriverHeaderFacts } from '../components/DriverHeaderFacts'
import { DriverDocumentsCompact } from '../components/DriverDocumentsCompact'
import { AssignedDocumentsPanel } from '../components/AssignedDocumentsPanel'
import { DocumentSection } from '../components/DocumentSection'
import { useDriverAssignments } from '../hooks/useDriverAssignments'
import { CellCopy } from '../components/CellCopy'
import { QuoSmsButton } from '../components/QuoSmsButton'
import { BoxTruckFilterSelect } from '../components/BoxTruckFilterSelect'
import { useBoxTruckLeadSources } from '../hooks/useBoxTruckLeadSources'
import { useDriverGroupBadges } from '../hooks/useDriverGroupBadges'
import { useDriverTeamGroups } from '../hooks/useDriverTeamGroups'
import { useRestoredListScroll } from '../hooks/useRestoredListScroll'
import { boxTruckRegistrationCompleteness } from '../utils/registration'
import {
    BoxTruckVerification,
    fetchAssignedOwnerDocumentBlobUrl,
    fetchAssignedOwnerDocuments,
    fetchAssignedVehicleDocumentBlobUrl,
    fetchAssignedVehicleDocuments,
    fetchBoxTruckRecruitSmsTemplate,
    fetchMyBoxTruckDrivers,
    fetchMyBoxTruckDriverHrManagers,
} from '../services/boxTruckLoadsApi'

const PAGE_SIZE = 50
const HEADERS = ['Driver', 'Phone', 'Equipment', 'Lic / Ins exp.', 'Last location', 'Status', 'Manager', 'Availability', 'Note', 'Action', '']

/** True when a free-text expiration date (ISO or "Month DD, YYYY") is in the past — highlights expired
 *  license/insurance dates in red. Empty/unparseable → false (rendered plainly). */
const isDateExpired = (v?: string): boolean => {
    if (!v) return false
    const t = new Date(v).getTime()
    return !Number.isNaN(t) && t < Date.now()
}

export const BoxTruckMyDriversPage = () => {
    const [items, setItems] = useState<BoxTruckVerification[]>([])
    // The group badge of every driver this user may see, in ONE request on mount, for every role. It draws the
    // badge on each row AND supplies the Group filter's options, so the filter offers exactly the desks the rows
    // show. 'all' rather than the loaded ids: the filter needs every desk, not just the ones on the first page.
    const groupBadges = useDriverGroupBadges('all')
    const teamGroups = useDriverTeamGroups(groupBadges)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [selected, setSelected] = useState<BoxTruckVerification | null>(null)
    const openDriver = useCallback((v: BoxTruckVerification) => {
        setSelected(v)
    }, [])
    // The team's saved recruiting SMS template (null = not customized yet → the compose seeds the bundled
    // default). Loaded once; updated in place when an operator saves an edit as the new default.
    const [recruitTemplate, setRecruitTemplate] = useState<string | null>(null)
    const [searchInput, setSearchInput] = useState('')
    const search = useDebouncedValue(searchInput, 300)
    // Server-side filters — every change reloads page 1 from the API (never client-side on the loaded page).
    const [equipmentFilter, setEquipmentFilter] = useState('')
    // Manager filter = the HR manager (who claimed the driver into My Drivers); value = hrManager.userId.
    const [managerFilter, setManagerFilter] = useState('')
    /**
     * The Manager and Source dropdowns' options, fetched when somebody first reaches for one.
     *
     * Both used to load on mount, which made opening this page four requests before the list. The managers
     * one is the expensive half: it reads every claimed driver record in the tenant (capped at 10 000) and
     * throws all but `boxTruck.hrManager` away, to build a list of names — on every page open, for the small
     * share of them that go on to filter by manager. Nothing depends on the options until the dropdown is
     * opened: the filters start empty, so no selected value is waiting for a label.
     */
    const [optionsWanted, setOptionsWanted] = useState(false)
    // Distinct HR managers across the fleet — the Manager dropdown's options.
    const [managers, setManagers] = useState<{ userId: string; name: string }[]>([])
    // Expiry filter: '' = all, per-field license/insurance expired/valid.
    const [expiryFilter, setExpiryFilter] = useState('')
    // Source filter = the driver's lead source (how they were added); '' = all.
    const [sourceFilter, setSourceFilter] = useState('')
    // Defaults + the team's custom lead sources — the Source dropdown's options (shared hook).
    const { sources: leadSourceOptions } = useBoxTruckLeadSources(optionsWanted)
    // Group filter = the team group the driver belongs to; value = group id, '' = all. For every role: the
    // options are the groups this user already sees on their rows (`useDriverTeamGroups`), and the control
    // renders only when there is at least one.
    const [groupFilter, setGroupFilter] = useState('')
    const hasMore = items.length < total
    // seqRef: only the LATEST load applies (an in-flight append can't land after a search reset and append
    // stale rows). loadingRef: synchronous latch so a scroll burst can't double-fire the same append page.
    const seqRef = useRef(0)
    const loadingRef = useRef(false)
    // Opening a driver replaces the list with that driver's detail, so the list keeps its place instead of
    // snapping back to the top every time the operator checks a row.
    const listRef = useRef<HTMLDivElement | null>(null)
    const { onScroll: rememberScroll, reset: resetScroll } = useRestoredListScroll(listRef, !!selected)

    const load = useCallback(
        async (
            targetPage: number,
            searchTerm: string,
            append: boolean,
            filters: { equipment?: string; manager?: string; expiry?: string; source?: string; group?: string }
        ) => {
            if (append && loadingRef.current) return
            loadingRef.current = true
            const seq = ++seqRef.current
            if (append) setLoadingMore(true)
            else setLoading(true)
            setError(null)
            try {
                const { items: rows, total: count } = await fetchMyBoxTruckDrivers(targetPage, PAGE_SIZE, searchTerm, filters)
                if (seq !== seqRef.current) return
                setItems((prev) => (append ? [...prev, ...rows] : rows))
                setTotal(count)
                setPage(targetPage)
            } catch (e) {
                if (seq === seqRef.current) setError(getUserErrorMessage(e) || 'Failed to load drivers')
            } finally {
                if (seq === seqRef.current) {
                    if (append) setLoadingMore(false)
                    else setLoading(false)
                }
                loadingRef.current = false
            }
        },
        []
    )

    // Reload page 1 whenever the (debounced) search term or a filter changes — all filtering is server-side.
    // A re-listed set starts at the top, so the remembered offset is meaningless: keeping it would restore a
    // deep scroll onto a short filtered list the next time the operator came back from a driver.
    useEffect(() => {
        resetScroll()
        void load(1, search, false, {
            equipment: equipmentFilter || undefined,
            manager: managerFilter || undefined,
            expiry: expiryFilter || undefined,
            source: sourceFilter || undefined,
            group: groupFilter || undefined,
        })
    }, [load, resetScroll, search, equipmentFilter, managerFilter, expiryFilter, sourceFilter, groupFilter])

    // The owner and the vehicle the OPEN driver is attached to — keyed by driver inside the hook, so a late
    // answer for the previous one can never paint over this one. It runs once per driver opened and not at
    // all while the list is scrolled: the list is a page of fifty, and resolving an owner and a vehicle per
    // row would be fifty times a cost one driver at a time pays once. Their documents are NOT part of it;
    // each panel below fetches its own when the operator asks.
    const assignments = useDriverAssignments(selected?.userId)
    // Seated on no vehicle: the section shows the vehicle documents the driver handed in instead.
    const noVehicle = assignments.data !== null && !assignments.data.vehicle

    // Load the distinct HR managers for the Manager dropdown (best-effort — empty options on failure), once
    // the operator has reached for a filter. See `optionsWanted`.
    useEffect(() => {
        if (!optionsWanted) return
        fetchMyBoxTruckDriverHrManagers()
            .then(setManagers)
            .catch(() => setManagers([]))
    }, [optionsWanted])

    // Load the team's saved recruiting SMS template once (best-effort; on failure the compose seeds the
    // bundled default).
    useEffect(() => {
        fetchBoxTruckRecruitSmsTemplate()
            .then(setRecruitTemplate)
            .catch(() => setRecruitTemplate(null))
    }, [])

    const loadMore = useCallback(() => {
        if (loading || loadingMore || items.length >= total) return
        void load(page + 1, search, true, {
            equipment: equipmentFilter || undefined,
            manager: managerFilter || undefined,
            expiry: expiryFilter || undefined,
            source: sourceFilter || undefined,
            group: groupFilter || undefined,
        })
    }, [load, loading, loadingMore, items.length, total, page, search, equipmentFilter, managerFilter, expiryFilter, sourceFilter, groupFilter])

    const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
        rememberScroll(e)
        const el = e.currentTarget
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) loadMore()
    }

    if (selected) {
        const regPct = boxTruckRegistrationCompleteness(selected.boxTruckInfo.info)
        return (
            <div className="flex h-full flex-col bg-background text-foreground">
                <div className="flex flex-wrap items-center gap-3 border-b border-border px-6 py-3">
                    <Button variant="ghost" size="sm" iconOnly onClick={() => setSelected(null)} aria-label="Back to my drivers">
                        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <div className="min-w-0 flex-1">
                        <h1 className="truncate text-base font-semibold">{selected.boxTruckInfo.info?.name || selected.name || 'Driver'}</h1>
                        <p className="text-xs text-foreground-muted">Registration, documents and messaging</p>
                    </div>
                    {/* Both the selection and the list row are patched: the detail is rendered from `selected`,
                        so updating only `items` would leave the header showing the fix the operator just
                        replaced until they navigated away and back. */}
                    <DriverHeaderFacts
                        driver={selected}
                        onLocationSaved={(loc) => {
                            const patch = { lastLocation: loc, profileLocation: { city: loc.city, state: loc.state } }
                            setSelected({ ...selected, ...patch })
                            setItems((prev) => prev.map((row) => (row.userId === selected.userId ? { ...row, ...patch } : row)))
                        }}
                    />
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-1.5" title="Registration completeness">
                        <div className="w-20">
                            <ProgressBar size="sm" value={regPct} color={regPct === 100 ? 'success' : 'accent'} />
                        </div>
                        <span className="text-xs font-semibold tabular-nums text-foreground">{regPct}%</span>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="flex min-h-full flex-col space-y-4">
                        <MyDriverQuickViewWithCompanyDeposit
                            driver={selected}
                            assignments={assignments.data}
                            assignmentsFailed={assignments.failed}
                            onRetryAssignments={assignments.reload}
                        />
                        {/* Documents get the FULL width, and Telegram sits under them.

                            They shared a two-column row, which gave each of them half the page — and the
                            documents are the half that needs it: a chip is a file name, so a narrow column
                            truncates every one of them to the point where two scans of the same licence are
                            indistinguishable. Across the full width the type groups sit three to a row and the
                            names are readable. Telegram is one field and a button; it loses nothing below. */}
                        <div className="space-y-4 rounded-2xl bg-surface p-5 shadow-surface">
                            <DocumentSection icon={<IdCard size={16} />} title="Driver documents" hint="Handed in by this driver">
                                <DriverDocumentsCompact driverId={selected.userId} docs={selected.boxTruckInfo.info?.documents ?? []} />
                            </DocumentSection>
                            {/* A hairline between sections rather than three cards: these are three kinds of
                                paperwork about one driver, and three cards read as three unrelated panels. */}
                            <div className="h-px bg-border" />
                            <AssignedDocumentsPanel
                                // Keyed by driver: the panel holds the documents it fetched in local state,
                                // and today only an unmount clears it. That is an invariant of how the page
                                // happens to navigate, not of the component — the day a "next driver" control
                                // lands in the detail, an unkeyed panel shows the previous driver's files.
                                key={`owner-${selected.userId}`}
                                icon={<Building2 size={16} />}
                                scope="owner"
                                title="Company documents"
                                hint="The owner this driver is attached to"
                                actionLabel="See company documents"
                                labels={BOX_TRUCK_OWNER_DOC_LABELS}
                                load={() => fetchAssignedOwnerDocuments(selected.userId)}
                                fetchBlobUrl={(documentId) => fetchAssignedOwnerDocumentBlobUrl(selected.userId, documentId)}
                                emptyText="No documents on this owner yet."
                                unavailableText={assignments.data && !assignments.data.owner ? 'This driver is not attached to an owner.' : undefined}
                            />
                            <div className="h-px bg-border" />
                            <AssignedDocumentsPanel
                                key={`vehicle-${selected.userId}`}
                                icon={<Truck size={16} />}
                                scope="vehicle"
                                title="Vehicle documents"
                                hint={noVehicle ? 'Handed in by this driver — no vehicle is assigned yet' : 'The asset this driver is seated on'}
                                actionLabel="See vehicle documents"
                                labels={BOX_TRUCK_ASSET_DOC_LABELS}
                                load={() => fetchAssignedVehicleDocuments(selected.userId)}
                                fetchBlobUrl={(documentId) => fetchAssignedVehicleDocumentBlobUrl(selected.userId, documentId)}
                                emptyText={noVehicle ? 'This driver has not sent any vehicle documents.' : 'No documents on this vehicle yet.'}
                            />
                        </div>
                        {selected.claimDriverId && (
                            <MessagingServicesSection
                                driver={{ id: selected.claimDriverId, telegramGroup: selected.telegramGroup }}
                                onTelegramGroupChange={(group) => {
                                    const updated = { ...selected, telegramGroup: group }
                                    setSelected(updated)
                                    setItems((prev) => prev.map((v) => (v.userId === selected.userId ? updated : v)))
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="flex h-full flex-col bg-background text-foreground">
            {/* Toolbar — atrek-style: everything left-aligned, no page title (the sidebar names the page). */}
            <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
                <span className="flex h-10 items-center whitespace-nowrap rounded-xl border border-border bg-surface px-4 text-sm font-semibold tabular-nums text-foreground">
                    Total: {loading ? '…' : total.toLocaleString()}
                </span>
                <div className="relative">
                    <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" strokeWidth={1.75} />
                    <input
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search driver (name, email, phone)"
                        className="h-10 w-80 rounded-xl bg-surface pl-9 pr-3 text-sm text-foreground shadow-field outline-none focus:ring-2 focus:ring-accent"
                    />
                </div>
                <BoxTruckFilterSelect
                    value={equipmentFilter}
                    onChange={setEquipmentFilter}
                    className="w-44"
                    options={[{ label: 'Equipment: All', value: '' }, ...BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS.map((label) => ({ label, value: label }))]}
                />
                <BoxTruckFilterSelect
                    onFirstOpen={() => setOptionsWanted(true)}
                    value={managerFilter}
                    onChange={setManagerFilter}
                    className="w-44"
                    options={[{ label: 'Manager: All', value: '' }, ...managers.map((m) => ({ label: m.name, value: m.userId }))]}
                />
                <BoxTruckFilterSelect
                    value={expiryFilter}
                    onChange={setExpiryFilter}
                    className="w-44"
                    options={[
                        { label: 'Expiry: All', value: '' },
                        { label: 'License expired', value: 'license_expired' },
                        { label: 'License valid', value: 'license_valid' },
                        { label: 'Insurance expired', value: 'insurance_expired' },
                        { label: 'Insurance valid', value: 'insurance_valid' },
                    ]}
                />
                <BoxTruckFilterSelect
                    onFirstOpen={() => setOptionsWanted(true)}
                    value={sourceFilter}
                    onChange={setSourceFilter}
                    className="w-40"
                    options={[{ label: 'Source: All', value: '' }, ...leadSourceOptions.map((s) => ({ label: s, value: s }))]}
                />
                {teamGroups.length > 0 && (
                    <BoxTruckFilterSelect
                        value={groupFilter}
                        onChange={setGroupFilter}
                        className="w-40"
                        options={[{ label: 'Group: All', value: '' }, ...teamGroups.map((g) => ({ label: g.name, value: g.id }))]}
                    />
                )}
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto" onScroll={onScroll}>
                {error ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <p className="text-sm font-medium text-danger">Couldn’t load drivers</p>
                        <p className="mt-1 max-w-sm text-xs text-foreground-muted">{error}</p>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                void load(1, search, false, {
                                    equipment: equipmentFilter || undefined,
                                    manager: managerFilter || undefined,
                                    expiry: expiryFilter || undefined,
                                    source: sourceFilter || undefined,
                                    group: groupFilter || undefined,
                                })
                            }
                            className="mt-4"
                        >
                            Retry
                        </Button>
                    </div>
                ) : loading && items.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center py-24">
                        <Spinner color="accent" size={24} />
                        <span className="ml-2 text-sm text-foreground-muted">Loading drivers…</span>
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <p className="text-sm font-medium text-foreground">No drivers yet</p>
                        <p className="mt-1 max-w-sm text-xs text-foreground-muted">Claim a driver from Profiles → Driver Verifications to see them here.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1140px] border-collapse text-[13px] leading-tight">
                            <thead>
                                <tr className="border-b border-border">
                                    {HEADERS.map((h, i) => (
                                        <th
                                            key={h || i}
                                            className="whitespace-nowrap px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-foreground-muted"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((v) => {
                                    const badge = boxTruckStatusBadge(v.boxTruckInfo.status, hasAppAccount(v))
                                    const hasPhone = !!v.boxTruckInfo.info?.phone
                                    const teamGroup = v.claimDriverId ? groupBadges.get(v.claimDriverId) : undefined
                                    return (
                                        <tr key={v.userId} className="cursor-pointer border-b border-border/60 hover:bg-surface" onClick={() => openDriver(v)}>
                                            <td className="px-3 py-1.5">
                                                <div className="flex items-center gap-1 text-[15px] font-bold text-foreground">
                                                    <span className="truncate">{v.boxTruckInfo.info?.name || v.name || '—'}</span>
                                                    {(v.boxTruckInfo.info?.name || v.name) && (
                                                        <CellCopy value={v.boxTruckInfo.info?.name || v.name || ''} label="name" />
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 text-xs text-foreground-muted">
                                                    {teamGroup && (
                                                        <span className="max-w-28">
                                                            <TeamGroupBadge group={teamGroup} truncate />
                                                        </span>
                                                    )}
                                                    <span className="truncate">{v.email}</span>
                                                    {v.email && <CellCopy value={v.email} label="email" />}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-foreground-muted">
                                                <div className="flex items-center gap-1">
                                                    <span>{v.boxTruckInfo.info?.phone || '—'}</span>
                                                    {v.boxTruckInfo.info?.phone && <CellCopy value={v.boxTruckInfo.info.phone} label="phone" />}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-foreground-muted">
                                                <div>{v.boxTruckInfo.info?.equipmentType || '—'}</div>
                                                {v.boxTruckInfo.info?.maxCargoWeightLbs != null && (
                                                    <div className="text-xs text-foreground-muted/80">
                                                        Payload: {v.boxTruckInfo.info.maxCargoWeightLbs.toLocaleString()} lbs
                                                    </div>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-xs text-foreground-muted">
                                                <div>
                                                    <span className="text-foreground-muted/60">Lic </span>
                                                    <span
                                                        className={
                                                            isDateExpired(v.boxTruckInfo.info?.licenseExpirationDate) ? 'font-medium text-danger' : undefined
                                                        }
                                                    >
                                                        {v.boxTruckInfo.info?.licenseExpirationDate || '—'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-foreground-muted/60">Ins </span>
                                                    <span
                                                        className={
                                                            isDateExpired(v.boxTruckInfo.info?.insuranceExpirationDate) ? 'font-medium text-danger' : undefined
                                                        }
                                                    >
                                                        {v.boxTruckInfo.info?.insuranceExpirationDate || '—'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-foreground-muted" onClick={(e) => e.stopPropagation()}>
                                                <BoxTruckLastLocationCell
                                                    lastLocation={v.lastLocation}
                                                    profileLocation={v.profileLocation}
                                                    // A location belongs to an ACCOUNT. With none, `userId` is the fleet record id, the
                                                    // correction endpoint resolves through live claims and answers 404 every time — so the
                                                    // control is not offered rather than offered and guaranteed to fail.
                                                    editable={
                                                        canEditLocation(v)
                                                            ? {
                                                                  userId: v.userId,
                                                                  onSaved: (loc) =>
                                                                      setItems((prev) =>
                                                                          prev.map((row) =>
                                                                              row.userId === v.userId
                                                                                  ? {
                                                                                        ...row,
                                                                                        lastLocation: loc,
                                                                                        profileLocation: { city: loc.city, state: loc.state },
                                                                                    }
                                                                                  : row
                                                                          )
                                                                      ),
                                                              }
                                                            : undefined
                                                    }
                                                />
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5">
                                                <Badge variant="soft" type={badge.type} label={badge.label} />
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-foreground-muted">{v.hrManager?.name || '—'}</td>
                                            <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                                                <DriverAvailabilityCell
                                                    userId={v.userId}
                                                    availability={v.availability}
                                                    onSaved={(availability) =>
                                                        setItems((prev) => prev.map((r) => (r.userId === v.userId ? { ...r, availability } : r)))
                                                    }
                                                />
                                            </td>
                                            <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                                                <DriverNoteCell
                                                    userId={v.userId}
                                                    note={v.teamNote}
                                                    onSaved={(teamNote) =>
                                                        setItems((prev) => prev.map((r) => (r.userId === v.userId ? { ...r, teamNote } : r)))
                                                    }
                                                />
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center gap-2">
                                                    {hasPhone && (
                                                        /* The recruiting pitch, prefilled, sent from the operator's own Quo line. */
                                                        <QuoSmsButton
                                                            phone={v.boxTruckInfo.info!.phone!}
                                                            text={`Hi ${(v.boxTruckInfo.info?.name || v.name || 'driver').split(' ')[0]}, ${recruitTemplate ?? BOX_TRUCK_RECRUIT_SMS}`}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-right">
                                                <Button variant="outline" size="sm" prefix={<FileText size={14} />} onClick={() => openDriver(v)}>
                                                    Manage
                                                </Button>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {loadingMore && (
                    <div className="flex items-center justify-center gap-2 py-4 text-xs text-foreground-muted">
                        <Spinner color="accent" size={16} /> Loading more…
                    </div>
                )}
                {!loading && items.length > 0 && (
                    <div className="px-6 py-3 text-center text-[11px] text-foreground-muted">
                        Showing {items.length} of {total}
                        {hasMore ? '' : ' · all drivers loaded'}
                    </div>
                )}
            </div>
        </div>
    )
}
